/**
 * Helper: Garage
 * -----------------------------------------------------------------------------
 * Garage helper utility functions.
 *
 */
import CryptoJS from 'crypto-js'

import { convertToHandle, convertToTitleCase } from '~/helpers/convert'

/**
 * Fetch data from custom APIs.
 * @param {Object} data - Data to build fetch from.
 * @param {Object} data.bodyData - Key-value pairs to add to body of fetch.
 * @param {Boolean} data.ignoreCache - Ignore response caching.
 * @param {String} data.secretKey - Secret key for payload value encryption.
 * @param {Function} data.signal - Abort controller.
 * @param {String} data.url - API URL.
 * @returns {Promise}
 */
export function fetchApiData({ bodyData = {}, ignoreCache, secretKey, signal, url } = {}) {
  return new Promise(async(resolve, reject) => {
    try {
      const lookup = cnvs.credentials.customApis.customerUpdate !== url

      const cacheId = convertToHandle(
        bodyData.registrationNumber ||
        bodyData.vinNumber ||
        bodyData.name,
      )

      if (
        lookup &&
        cnvs.settings.caching.lookupCache &&
        !ignoreCache &&
        !cnvs.settings.caching.disable &&
        !cnvs.storybook
      ) {
        const cache = JSON.parse(sessionStorage.getItem(cnvs.settings.caching.lookupCache))

        if (cache) {
          const match = cache[cacheId]

          if (match) {
            resolve(match)
            return
          }
        }
      }

      /**
       * Build headers.
       */
      const headers = new Headers()
      headers.append('Referer', cnvs.store.permanentDomain?.replace('//, '))
      headers.append('Content-Type', 'application/x-www-form-urlencoded')

      /**
       * Build body.
       */
      const body = new URLSearchParams()

      for await (const [key, value] of Object.entries(bodyData)) {
        const stringValue = value?.toString()

        const encodedValue = secretKey && stringValue
          ? CryptoJS.AES.encrypt(stringValue, secretKey).toString()
          : value

        body.append(key, encodedValue)
      }

      /**
       * Fetch response.
       */
      const response = await fetch(url, {
        body,
        headers,
        method: 'POST',
        signal,
      })

      if (!response.ok) {
        reject(response.status)
        return
      }

      const jsonResponse = await response.json()

      /**
       * Cache response for lookups if setting enabled.
       * - Also only cache if response message doesn't exist or it's 'found'.
       */
      if (
        lookup &&
        cnvs.settings.caching.lookupCache &&
        (!jsonResponse.message || jsonResponse.message === 'found')
      ) {
        const cache = JSON.parse(sessionStorage.getItem(cnvs.settings.caching.lookupCache)) || {}
        cache[cacheId] = jsonResponse

        sessionStorage.setItem(
          cnvs.settings.caching.lookupCache,
          JSON.stringify(cache),
        )
      }

      resolve(jsonResponse)

    } catch (error) {
      if (error.name === 'AbortError') {
        resolve()
        return
      }

      reject(error)
      cnvs.ReportError('Failed to fetch custom API response', error)
    }
  })
}

/**
 * Build blank vehicle object.
 * @returns {import("~stores/garage").Vehicle}
 */
function buildBlankVehicle() {
  const details = {}

  Object.values(cnvs.vehicleFinder.vehicleDetails).forEach((vehicleDetail) => {
    if (!vehicleDetail.metafield) {
      return
    }

    const stringType = [
      cnvs.vehicleFinder.handles.type,
      cnvs.vehicleFinder.handles.vrmId,
    ].includes(vehicleDetail.handle)

    details[vehicleDetail.handle] = stringType ? '' : []
  })

  return {
    details,
    id: '',
    method: '',
    motUpdated: 0,
    providerData: {
      make: [],
      model: [],
      year: [],
    },
    query: '',
    user: {
      mot: '',
      vin: '',
      vrm: '',
    },
  }
}

/**
 * Build compatibility data from compatibility string.
 * @param {String} compatibility - Compatibility.
 * @returns {Object}
 */
export function buildCompatibility(compatibility) {
  switch (compatibility) {
    case 'compatible':
      return {
        colour: '--color-system-success-light-mode',
        drawerColour: '--color-system-success-light-mode',
        icon: 'icon-tick',
        text: {
          component: cnvs.strings.compatibility.compatible,
          drawer: cnvs.strings.compatibility.drawer.matches,
        },
        textMode: 'dark',
        type: 'compatible',
      }

    case 'incompatible':
      return {
        colour: '--color-brand-red',
        drawerColour: '--color-brand-red',
        icon: 'icon-cross',
        text: {
          component: cnvs.strings.compatibility.incompatible,
          drawer: cnvs.strings.compatibility.drawer.no_match,
        },
        textMode: 'dark',
        type: 'incompatible',
      }

    case 'maybe':
      return {
        colour: '--color-brand-true-black',
        drawerColour: '--color-neutral-2',
        icon: 'icon-info',
        text: {
          component: cnvs.strings.compatibility.maybe,
          drawer: cnvs.strings.compatibility.drawer.possible_match,
        },
        textMode: 'light',
        type: 'maybe',
      }

    default:
      return {
        colour: '--color-neutral-2',
        drawerColour: '--color-neutral-2',
        icon: '',
        text: {
          component: '',
          drawer: cnvs.strings.compatibility.drawer.check_vehicle,
        },
        textMode: 'light',
        type: 'none',
      }
  }
}

/**
 * Build event parameters.
 * @param {import("~stores/garage").FormattedVehicle} vehicle - Vehicle data.
 * @returns {Object}
 */
export function buildEventParameters(vehicle) {
  const engine = cnvs.vehicleFinder.handles.engine
  const make = cnvs.vehicleFinder.handles.make
  const model = cnvs.vehicleFinder.handles.model
  const subModel = cnvs.vehicleFinder.handles.subModel
  const type = cnvs.vehicleFinder.handles.type
  const year = cnvs.vehicleFinder.handles.year

  return {
    vehicle_search_engine: formatVehicleDetailValue(vehicle.details?.[engine]),
    vehicle_search_form: formatVehicleDetailValue(vehicle.form),
    vehicle_search_make: formatVehicleDetailValue(vehicle.details?.[make]),
    vehicle_search_model: formatVehicleDetailValue(vehicle.details?.[model]),
    vehicle_search_sub_model: formatVehicleDetailValue(vehicle.details?.[subModel]),
    vehicle_search_type: formatVehicleDetailValue(vehicle.details?.[type]),
    vehicle_search_year: formatVehicleDetailValue(vehicle.details?.[year]),
  }
}

/**
 * Build array of metafield identifiers for taxonomy metafields.
 * @returns {Array}
 */
export function buildTaxonomyMetafieldIdentifiers() {
  return Object.values(cnvs.vehicleFinder.vehicleDetails)
    .filter((vehicleDetail) => vehicleDetail.metafield)
    .map((vehicleDetail) => {
      return {
        namespace: vehicleDetail.namespace,
        key: vehicleDetail.key,
      }
    })
    .flat()
}

/**
 * Build vehicle ID from vehicle details and user details.
 * - If method is passed then vehicle is assumed to be a string to convert.
 * @param {String|import("~stores/garage").Vehicle} vehicle - Vehicle object or
 * string to convert.
 * @param {String} [method] - Override method.
 * @returns {import("~stores/garage").VehicleId}
 */
export function buildVehicleId(vehicle, method) {
  const methodToUse = method ? method : vehicle.method
  const vrmIdHandle = cnvs.vehicleFinder.handles.vrmId
  let string = ''

  switch (methodToUse) {
    case 'vin':
      string = method ? vehicle : `${vehicle.user.vin}-${vehicle.details[vrmIdHandle]}`
      return string.toUpperCase().replaceAll(' ', '')

    case 'vrm':
      string = method ? vehicle : `${vehicle.user.vrm}-${vehicle.details[vrmIdHandle]}`
      return string.toUpperCase().replaceAll(' ', '')

    default:
      return Object.values(vehicle.details)
        .filter(Boolean)
        .map((value) => {
          const formattedValue = typeof value === 'object'
            ? value.join('_')
            : value

          return String(formattedValue).toUpperCase().replaceAll(' ', '_')
        })
        .join('_')
  }
}

/**
 * Example formatted vehicle for testing.
 * - Returns random object.
 * @param {Boolean} format - Format vehicle data.
 * @param {String} method - Submission method.
 * @param {String} type - Type of vehicle.
 * @returns {import("~stores/garage").Vehicle}
 */
export function exampleVehicle(format = true, method = 'vrm', type = 'Car') {
  const vehicles = [
    {
      ...buildBlankVehicle(),
      details: {
        engine: '1.1L 3-cylinder',
        fuel: 'Petrol',
        gearbox: 'Manual',
        make: 'Ford',
        model: 'Fiesta',
        type,
        year: '2017',
      },
      method,
      user: {
        vin: '12345678890',
        vrm: 'AB23 DEF',
      },
    },
    {
      ...buildBlankVehicle(),
      details: {
        engine: '1.5 Skyactiv-G',
        fuel: 'Petrol',
        gearbox: 'Manual',
        make: 'Mazda',
        model: '2',
        type,
        year: '2022',
      },
      method,
      user: {
        vin: '12345678890',
        vrm: 'HX72 KVP',
      },
    },
    {
      ...buildBlankVehicle(),
      details: {
        engine: 'Mini Engine',
        fuel: 'Petrol',
        gearbox: 'Manual',
        make: 'Mini',
        model: 'Cooper',
        type,
        year: '2018',
      },
      method,
      user: {
        vin: '12345678890',
        vrm: 'AB12 ABC',
      },
    },
  ]

  const vehicle = vehicles[Math.floor(Math.random() * vehicles.length)]
  vehicle.id = buildVehicleId(vehicle)
  return format ? formatVehicle(vehicle) : vehicle
}

/**
 * Determine if vehicle detail is compatible with product detail.
 * - For use in product details entries forloop.
 * - Returns values with matching value(s) highlighted.
 * @param {String} detailsKey - Product detail key.
 * @param {Array} detailsValue - Product detail value.
 * @param {Object} activeVehicleDetails - Vehicle details.
 * @returns {Object}
 */
export function getVehicleCompatibility(detailsKey, detailsValue, activeVehicleDetails) {
  // eslint-disable-next-line no-unused-vars
  let handle = Object.entries(cnvs.vehicleFinder.vehicleDetails).find(([_, value]) => {
    return value.key === detailsKey
  })

  if (!handle || !detailsValue) {
    return {
      handle: false,
      matches: false,
      values: detailsValue,
    }
  }

  handle = handle[0]

  /**
   * If active vehicle is missing a detail then at best it can be a partial
   * match with the product data.
   */
  const data = {
    handle,
    matches: 'none',
    values: detailsValue,
  }

  if (!activeVehicleDetails) {
    return data
  }

  const activeVehicleDetail = activeVehicleDetails[handle]
  const editorialYearHandle = cnvs.vehicleFinder.handles.editorialYear
  const subModelHandle = cnvs.vehicleFinder.handles.subModel
  const typeHandle = cnvs.vehicleFinder.handles.type

  const hasActiveVehicleDetail = Boolean(
    (typeof activeVehicleDetail === 'string' && activeVehicleDetail) ||
    (typeof activeVehicleDetail === 'object' && activeVehicleDetail?.length),
  )

  /**
   * If active vehicle has detail then it must match to be compatible.
   * - Format values to highlight which ones match.
   * - Values are not highlighted if there's only one.
   */
  if (hasActiveVehicleDetail) {
    const formattedValues = []
    let matches = false

    /**
     * All active vehicle years must appear in product years to be compatible.
     * - If active vehicle is set to 'All years' then it's compatible.
     */
    if (isYearHandle(handle)) {
      if (activeVehicleDetail.includes(cnvs.compiledStrings.vehicle_finder.dropdowns.all_years)) {
        matches = true
      } else {
        const activeVehicleDetailArray = Array.isArray(activeVehicleDetail)
          ? activeVehicleDetail
          : [activeVehicleDetail]

        matches = activeVehicleDetailArray.every((activeVehicleValue) => {
          return detailsValue.find((value) => {
            return String(value)?.trim() === String(activeVehicleValue)
          })
        })
      }

    /**
     * Otherwise check matching.
     */
    } else {
      detailsValue.forEach((value) => {
        const lowercaseValue = String(value)?.toLowerCase()?.trim()
        let valueMatches = false

        /**
         * For numbers/strings the value must match.
         */
        if (
          ['number', 'string'].includes(typeof activeVehicleDetail) &&
          lowercaseValue === String(activeVehicleDetail)?.toLowerCase()?.trim()
        ) {
          matches = true
          valueMatches = true
        }

        /**
         * For arrays at least one value must match to be compatible.
         */
        if (typeof activeVehicleDetail === 'object') {
          const arrayMatches = activeVehicleDetail.some((activeVehicleValue) => {
            return lowercaseValue === String(activeVehicleValue)?.toLowerCase()?.trim()
          })

          if (arrayMatches) {
            matches = true
            valueMatches = true
          }
        }

        /**
         * If vehicle type has types array then use them to match instead.
         * - Only one value must match to be compatible.
         */
        if (handle === typeHandle) {
          const vehicleType = activeVehicleDetails[typeHandle]

          const types = cnvs.vehicleFinder.types[vehicleType].map((type) => {
            return type.includes(':') ? type.split(':')[1] : type
          })

          if (types.length && types.includes(value)) {
            matches = true
            valueMatches = true
          }
        }

        /**
         * Add highlight if there is more than one value.
         */
        if (valueMatches && detailsValue.length > 1) {
          formattedValues.push(`<span class="is-match">${value}</span>`)
          return
        }

        formattedValues.push(value)
      })
    }

    data.matches = matches
      ? 'compatible'
      : 'incompatible'

    /**
     * If editorial year or sub-model doesn't match then it may still be
     * compatible.
     */
    if ([editorialYearHandle, subModelHandle].includes(handle) && !matches) {
      data.matches = 'maybe'
    }

    data.values = formattedValues
  }

  return data
}

/**
 * Return year handles array.
 * @returns {Array}
 */
function yearHandles() {
  return [
    cnvs.vehicleFinder.handles.editorialYear,
    cnvs.vehicleFinder.handles.searchableYear,
    cnvs.vehicleFinder.handles.year,
  ]
}

/**
 * Determine if handle is a year-type handle.
 * @param {String} handle - Handle to check.
 * @returns {Boolean}
 */
export function isYearHandle(handle) {
  return yearHandles().includes(handle)
}

/**
 * Validators.
 * -----------------------------------------------------------------------------
 * Validate vehicle and vehicle ID props.
 *
 */

/**
 * Validate vehicle ID.
 * @param {*} value - Passed prop value.
 * @returns {Boolean}
 */
export function validateVehicleId(value) {
  return typeof value === 'string'
}

/**
 * Validate vehicle object.
 * @param {*} value - Passed prop value.
 * @returns {Boolean}
 */
export function validateVehicle(value) {
  if (typeof value === 'boolean') {
    return true
  }

  return (
    typeof value === 'object' &&
    Object.keys(value?.details).length &&
    validateVehicleId(value?.id) &&
    Object.keys(value?.user).length
  )
}

/**
 * Converters.
 * -----------------------------------------------------------------------------
 * Convert VRM and VIN API data and dropdowns into vehicle object.
 *
 */

/**
 * Convert dropdowns response into vehicle.
 * - All values except 'type' should be an array to mimic VRM response.
 * - Set year handle values based on whichever year handle has been populated.
 * @param {Object} response - Dropdowns response.
 * @returns {import("~stores/garage").Vehicle}
 */
export function convertDropdownsToVehicle(response) {
  const vehicle = buildBlankVehicle()
  let yearValue = false

  Object.entries(response).forEach(([key, value]) => {
    if (value === 'empty') {
      return
    }

    if (isYearHandle(key) && !yearValue) {
      yearValue = [value]
    }

    vehicle.details[key] = key === cnvs.vehicleFinder.handles.type
      ? value
      : [value]
  })

  yearHandles().forEach((handle) => {
    if (vehicle.details[handle]?.length) {
      return
    }

    vehicle.details[handle] = yearValue
  })

  vehicle.form = 'Make-Model'
  vehicle.method = 'dropdowns'
  vehicle.id = buildVehicleId(vehicle)

  return vehicle
}

/**
 * Convert example.
 * @param {String} method - Form method.
 * @param {String} vehicleType - Vehicle type.
 * @returns {import("~stores/garage").Vehicle}
 */
export function convertExample(method = 'vrm', vehicleType = 'Car') {
  return exampleVehicle(false, method, vehicleType)
}

/**
 * Convert VIN response into vehicle.
 * @param {Object} data - Conversion data.
 * @param {String} data.query - Query details.
 * @param {Object} data.response - VIN response.
 * @param {String} data.vehicleType - Vehicle type.
 * @returns {import("~stores/garage").Vehicle}
 */
export function convertVinToVehicle(data) {
  const vehicle = convertVrmToVehicle(data)

  return {
    ...vehicle,
    form: 'VIN',
    method: 'vin',
  }
}

/**
 * Convert VRM response into MOT due date.
 * @param {Object} response - VRM response.
 * @returns {Date}
 */
export function convertVrmToMot() {
  // TODO: Use response to output MOT due date (US store)
  return ''
}

/**
 * Extract provider data (make, model, year) from VRM/VIN response.
 * @param {Object} response - Provider response.
 * @returns {Object}
 */
function extractProviderData(response) {
  const makeDetail = cnvs.vehicleFinder.vehicleDetails[cnvs.vehicleFinder.handles.make]
  const modelDetail = cnvs.vehicleFinder.vehicleDetails[cnvs.vehicleFinder.handles.model]
  const yearDetail = cnvs.vehicleFinder.vehicleDetails[cnvs.vehicleFinder.handles.year]

  const providerData = {
    make: [],
    model: [],
    year: [],
  }

  /**
   * Extract make
   */
  if (makeDetail?.response) {
    const makeValue = getDetailFromCriteria(response.criteria, 'make.description')
    if (makeValue) {
      providerData.make = [makeValue.trim()]
    }
  }

  /**
   * Extract model
   */
  if (modelDetail?.response) {
    const modelValue = getDetailFromCriteria(response.criteria, 'model.description')
    if (modelValue) {
      providerData.model = [modelValue.trim()]
    }
  }

  /**
   * Extract year
   */
  if (yearDetail?.response) {
    const yearValue = getDetailFromCriteria(response.criteria, 'application.date.manufacture')
    const yearString = String(yearValue)?.trim()

    /**
     * Check for space-separated multiple years (e.g., "2009 2010 2011")
     */
    if (yearString.includes(' ')) {
      providerData.year = yearString
        .split(/\s+/)
        .map(year => year.trim())
        .filter(Boolean)
    } else {
      const yearMatch = yearString.match(/\d{4}/)
      providerData.year = yearMatch ? [yearMatch[0]] : []
    }
  }

  return providerData
}

/**
 * Convert VRM response into vehicle.
 * @param {Object} data - Conversion data.
 * @param {String} data.query - Query details.
 * @param {Object} data.response - VRM response.
 * @param {String} data.vehicleType - Vehicle type.
 * @returns {import("~stores/garage").Vehicle}
 */
export function convertVrmToVehicle({ query, response = {}, vehicleType } = {}) {
  const vehicle = buildBlankVehicle()

  vehicle.form = 'VRM'
  vehicle.method = 'vrm'
  vehicle.query = query.query
  vehicle.user.vin = getDetailFromCriteria(response.criteria, 'id.vin')
  vehicle.user.vrm = query.registration

  vehicle.details = {}
  vehicle.details[cnvs.vehicleFinder.handles.type] = vehicleType

  const haynesPro = response.identifiers?.identifier?.find((identifier) => {
    return identifier.authority === 'HAYNESPRO'
  })

  if (haynesPro) {
    vehicle.details[cnvs.vehicleFinder.handles.vrmId] = haynesPro.id
  }

  vehicle.providerData = extractProviderData(response)

  /**
   * Set vehicle ID based on above data.
   */
  vehicle.id = buildVehicleId(vehicle)

  return vehicle
}

/**
 * Get detail from criteria array.
 * @param {Array} criteria - Criteria array from response.
 * @param {String} desc - Description to find.
 * @returns {String}
 */
function getDetailFromCriteria(criteria, desc) {
  const matchedCriteria = criteria?.find((crit) => crit.critDesc === desc)

  if (!matchedCriteria) {
    return ''
  }

  return matchedCriteria.critValueDesc
    ? matchedCriteria.critValueDesc
    : matchedCriteria.critValue
}

/**
 * Convert VRM ID metafield data into vehicle using existing lookup vehicle.
 * @param {Object} data - Conversion data.
 * @param {Object} data.product - Response product with VRM ID metafield data.
 * @param {import("~stores/garage").Vehicle} data.vehicle
 * @returns {Boolean|import("~stores/garage").Vehicle}
 */
export function convertVrmIdMetafieldToVehicle({ product, vehicle } = {}) {
  const vrmId = vehicle?.details[cnvs.vehicleFinder.handles.vrmId]
  const vehicles = product?.metafields[0]?.value

  if (!vehicles || !vrmId) {
    return false
  }

  const vrmIdData = JSON.parse(vehicles)
  const vehicleVrmIdData = vrmIdData[vrmId]

  if (!vehicleVrmIdData) {
    return false
  }

  const updatedVehicle = vehicle

  /**
   * Get correct response from VRM ID metafield data based on theme setting.
   */
  Object.values(cnvs.vehicleFinder.vehicleDetails).forEach((value) => {
    if (!value.response) {
      return
    }

    const responseValue = vehicleVrmIdData[value.response]

    if (!responseValue?.length) {
      return
    }

    updatedVehicle.details[value.handle] = responseValue
  })

  return updatedVehicle
}

/**
 * Formatters.
 * -----------------------------------------------------------------------------
 * Format vehicle object to add additional generated fields.
 *
 */

/**
 * Format vehicle object from converter into Vuex store definition.
 * - See stores/garage.js for type definitions.
 * @param {import("~stores/garage").Vehicle} vehicle - Vehicle to format.
 * @returns {Boolean|import("~stores/garage").FormattedVehicle}
 */
export function formatVehicle(vehicle) {
  if (!vehicle) {
    return false
  }

  /**
   * Determine if fuzzy search is being used.
   */
  const vrmIdHandle = cnvs.vehicleFinder.handles.vrmId
  const isFuzzySearch =
    ['vrm', 'vin'].includes(vehicle.method) &&
    !vehicle.details?.[vrmIdHandle] &&
    cnvs.vehicleFinder.enableFuzzySearch

  /**
   * Build strings.
   */
  const strings = formatStrings(vehicle)

  /**
   * Create filters and filter parameters.
   */
  const { filters, filterParams } = formatVehicleFilters(vehicle, isFuzzySearch)

  /**
   * Build results URL from filter parameters.
   */
  const resultsUrl = `${cnvs.routes.normalisedRoot}collections/${cnvs.vehicleFinder.resultsCollection}?${filterParams.join('&')}`

  /**
   * Return vehicle object.
   */
  return {
    ...vehicle,
    filters,
    isFuzzySearch,
    resultsUrl,
    strings,
  }
}

/**
 * Format vehicle strings.
 * @param {import("~stores/garage").Vehicle} vehicle - Vehicle object to format.
 * @returns {import("~stores/garage").VehicleStrings}
 */
function formatStrings(vehicle) {
  let title = cnvs.strings.garage.information.title

  let text = vehicle.method === 'dropdowns'
    ? cnvs.strings.garage.information.text_dropdowns
    : cnvs.strings.garage.information.text

  /**
   * Go through vehicle details replacing short codes.
   * - If no value for short code then don't replace in text.
   */
  Object.entries(vehicle.details).forEach(([key, value]) => {
    const formattedValue = formatVehicleDetailValue(value, key)
    title = title.replaceAll(`{{ ${key} }}`, formattedValue)
    text = text.replaceAll(`{{ ${key} }}`, formattedValue)
  })

  Object.entries(vehicle.user).forEach(([key, value]) => {
    const formattedValue = formatVehicleDetailValue(value, key)
    title = title.replaceAll(`{{ ${key} }}`, formattedValue)
    text = text.replaceAll(`{{ ${key} }}`, formattedValue)
  })

  /**
   * Split text and use delimiter.
   */
  text = text
    .split(',')
    .filter(Boolean)
    .join(cnvs.strings.garage.information.delimiter)

  /**
   * If short code still exists in text then set to blank so it's hidden.
   */
  text = text.includes('{{') ? '' : text

  /**
   * Final clean-up.
   */
  title = title?.trim()
    ?.replaceAll('(false)', '')
    ?.replaceAll('()', '')

  text = text?.trim()
    ?.replaceAll('(false)', '')
    ?.replaceAll('()', '')

  return {
    title,
    text,
  }
}

/**
 * Format vehicle detail value into string.
 * @param {Array<Number|String>|Number|String} value - Detail value.
 * @param {String} [handle] - Detail handle/key.
 * @returns {String}
 */
export function formatVehicleDetailValue(value, handle) {
  if (!value) {
    return value
  }

  let formattedValue = typeof value === 'string'
    ? value
    : value.join(cnvs.strings.garage.information.array_delimiter)

  if (isYearHandle(handle)) {
    formattedValue = formatYears(value)
  }

  return formattedValue
}

/**
 * Format year range from array of years.
 * @param {Array} years - Years to format.
 * @returns {String}
 */
export function formatYears(years) {
  if (typeof years !== 'object') {
    return years
  }

  if (!years?.length) {
    return ''
  }

  if (years.length === 1) {
    return years[0]
  }

  const ranges = [[]]
  let previousYear = 0
  let rangeIndex = 0
  const uniqueYears = []

  const sortedYears = years
    .sort()
    .filter((year) => {
      if (uniqueYears.includes(year)) {
        return false
      }

      uniqueYears.push(year)
      return true
    })

  sortedYears.forEach((year) => {
    const numYear = Number(year)

    /**
     * If new year is one greater than previous it's contiguous.
     * - Add to existing range array.
     */
    if (!previousYear || numYear - 1 === previousYear) {
      previousYear = numYear
      ranges[rangeIndex].push(numYear)
      return
    }

    /**
     * Otherwise it's non-contiguous and needs to be added to a new range array.
     */
    rangeIndex += 1
    ranges[rangeIndex] = []
    ranges[rangeIndex].push(numYear)
    previousYear = 0
  })

  return ranges
    .map((range) => {
      if (range.length === 1) {
        return range[0]
      }

      return `${range[0]} - ${range[range.length - 1]}`
    })
    .join(', ')
}

/**
 * Format vehicle filters.
 * @param {import("~stores/garage").Vehicle} vehicle - Vehicle object to format.
 * @param {Boolean} [isFuzzySearch] - Fuzzy search is being performed.
 * @returns {Object}
 */
function formatVehicleFilters(vehicle, isFuzzySearch) {
  const filterParams = []
  const vrmIdHandle = cnvs.vehicleFinder.handles.vrmId
  const typeHandle = cnvs.vehicleFinder.handles.type
  let detailsObject = vehicle.details

  const makeHandle = cnvs.vehicleFinder.handles.make
  const modelHandle = cnvs.vehicleFinder.handles.model
  const yearHandle = cnvs.vehicleFinder.handles.year

  /**
   * If vehicle VRM ID exists then use it as the only filter.
   */
  if (vehicle.details?.[vrmIdHandle]) {
    detailsObject = {
      [vrmIdHandle]: vehicle.details[vrmIdHandle],
    }
  } else if (isFuzzySearch) {
    detailsObject = {}

    if (vehicle.details[typeHandle]) {
      detailsObject[typeHandle] = vehicle.details[typeHandle]
    }

    if (vehicle.providerData.make?.length) {
      detailsObject[makeHandle] = vehicle.providerData.make
    }

    if (vehicle.providerData.model?.length) {
      detailsObject[modelHandle] = vehicle.providerData.model
    }

    if (vehicle.providerData.year?.length) {
      detailsObject[yearHandle] = vehicle.providerData.year
    }
  }

  /**
   * Build array of filters and filter params.
   */
  const filters = Object.entries(detailsObject)
    .flatMap(([key, values]) => {
      const excludedHandles = [
        cnvs.vehicleFinder.handles.editorialYear,
        cnvs.vehicleFinder.handles.subModel,
      ]

      /**
       * Exclude year handle when VRM ID exists.
       */
      if (vehicle.details?.[vrmIdHandle]) {
        excludedHandles.push(cnvs.vehicleFinder.handles.year)
      }

      /**
       * Remove excluded handles from filters.
       */
      if (excludedHandles.includes(key)) {
        return []
      }

      const vehicleDetail = cnvs.vehicleFinder.vehicleDetails[key]

      if (!values?.length || !values || !vehicleDetail) {
        return []
      }

      let formattedValues = typeof values === 'string' ? [values] : values

      /**
       * Add additional vehicle types to values.
       */
      if (key === typeHandle) {
        formattedValues = cnvs.vehicleFinder.types[formattedValues[0]]?.map((type) => {
          return type.includes(':') ? type.split(':')[1] : type
        })
      }

      /**
       * Output metafield filter for each value.
       */
      return formattedValues
        .flatMap((value) => {
          if (value === cnvs.compiledStrings.vehicle_finder.dropdowns.all_years) {
            return []
          }

          /**
           * For fuzzy search, generate multiple filter variations
           */
          if (isFuzzySearch && (key === makeHandle || key === modelHandle)) {
            const filterType = key === makeHandle ? 'make' : 'model'
            const variations = generateFuzzyFilterVariations(value, filterType)

            return variations.map((variation) => {
              filterParams.push(`${vehicleDetail.filter}=${encodeURIComponent(variation)}`)

              return {
                productMetafield: {
                  key: vehicleDetail.key,
                  namespace: vehicleDetail.namespace,
                  value: variation,
                },
              }
            })
          }

          /**
           * Standard filter for non-fuzzy searches
           */
          filterParams.push(`${vehicleDetail.filter}=${encodeURIComponent(value)}`)

          return {
            productMetafield: {
              key: vehicleDetail.key,
              namespace: vehicleDetail.namespace,
              value,
            },
          }
        })

    })

  return { filters, filterParams }
}

/**
 * Get array of vehicle types based on form.
 * @param {String} form - Form type.
 * @returns {Array}
 */
export function getVehicleTypes(form) {
  if (form === 'dropdowns') {
    return [
      'Car',
      'Motorcycles',
      'ATV',
      'Chainsaw',
      'Marine',
      'Mower',
      'Scooter',
      'Snowmobile',
      'Tractor',
    ].filter((type) => {
      return cnvs.vehicleFinder.enableTypes[type]
    })
  }

  const dropdownTypes = cnvs.vehicleFinder.dropdownTypes[form]
  const types = []

  Object.entries(dropdownTypes).forEach(([key, value]) => {
    if (!value) {
      return
    }

    types.push(key)
  })

  return types
}

/**
 * Get default vehicle type for current form.
 * - If default doesn't exist then select 'Car' type as it always has to exist.
 * @param {String} form - Form type.
 * @returns {String}
 */
export function getDefaultVehicleType(form) {
  const defaultType = cnvs.vehicleFinder.defaultTypes[form]

  if (defaultType === 'none') {
    return ''
  }

  const types = getVehicleTypes(form)

  if (types.includes(defaultType)) {
    return defaultType
  }

  return types[0]
}

/**
 * Get the source from a namespace string.
 * @param {String} namespace - Namespace string.
 * @returns {String}
 */
export function getSourceFromNamespace(namespace) {
  // Return the first matching known source in the namespace string
  if (namespace.includes('vehicle-finder')) {
    return 'vehicle-finder'
  }

  if (namespace.includes('vrm')) {
    return 'vrm'
  }

  if (namespace.includes('vin')) {
    return 'vin'
  }

  return namespace
}

/**
 * Generate fuzzy search filter variations for make/model.
 * Creates multiple filters with different casings to improve match rate.
 * @param {String} value - Original value from provider.
 * @param {String} type - 'make' or 'model'.
 * @returns {Array<String>}
 */
function generateFuzzyFilterVariations(value, type) {
  if (!value || typeof value !== 'string') {
    return []
  }

  const variations = new Set()
  const trimmedValue = value.trim()

  variations.add(trimmedValue)

  if (type === 'make') {
    variations.add(trimmedValue.toUpperCase())
    variations.add(convertToTitleCase(trimmedValue))
  } else if (type === 'model') {
    variations.add(convertToTitleCase(trimmedValue))

    /**
     * Add version without special characters (e.g., "MT-07 LA" → "MT07LA")
     */
    const noSpecialChars = trimmedValue.replace(/[\s\-&/()]/g, '')

    if (noSpecialChars !== trimmedValue) {
      variations.add(noSpecialChars)
      variations.add(noSpecialChars.toUpperCase())
      variations.add(convertToTitleCase(noSpecialChars))
    }

    const segments = trimmedValue.split(/\s+/).filter(seg => seg.length > 0)

    /**
     * Add each segment
     */
    segments.forEach(segment => {
      variations.add(segment)
      variations.add(convertToTitleCase(segment))

      const segmentNoSpecialChars = segment.replace(/[\s\-&/()]/g, '')
      if (segmentNoSpecialChars !== segment) {
        variations.add(segmentNoSpecialChars)
        variations.add(segmentNoSpecialChars.toUpperCase())
        variations.add(convertToTitleCase(segmentNoSpecialChars))
      }
    })

    /**
     * Add combinations of first N segments (2 and 3)
     */
    for (let count = 2; count <= Math.min(3, segments.length); count++) {
      const combined = segments.slice(0, count).join(' ')
      variations.add(combined)
      variations.add(convertToTitleCase(combined))

      const combinedNoSpecialChars = combined.replace(/[\s\-&/()]/g, '')
      if (combinedNoSpecialChars !== combined) {
        variations.add(combinedNoSpecialChars)
        variations.add(combinedNoSpecialChars.toUpperCase())
        variations.add(convertToTitleCase(combinedNoSpecialChars))
      }
    }

    /**
     * Add alphanumeric splits (e.g., "MT07LA" → "MT", "07", "LA")
     */
    const alphaNumParts = trimmedValue
      .split(/(?<=[a-zA-Z])(?=\d)|(?<=\d)(?=[a-zA-Z])/)
      .filter(Boolean)

    if (alphaNumParts.length > 1) {
      alphaNumParts.forEach((part) => {
        if ((/^[a-zA-Z]+$/).test(part)) {
          variations.add(part)
          variations.add(part.toUpperCase())
          variations.add(convertToTitleCase(part))
        }
      })
    }
  }

  return Array.from(variations)
}
